import * as React from 'react';
import { observer } from 'mobx-react';
import { Post } from '@sprinklr/stories/post/Post';
import { getEndianTime } from 'utils/Number/NumberUtils';
import styled from 'components/_UI/designSystem';
import NativeProfileLockup, { StyledTimeStamp, StyledLocation } from './Parts/NativeProfileLockup';
import NativeEngagement from './Parts/NativeEngagement';
import NativeText from './Parts/NativeText';
import NativeMedia from './Parts/NativeMedia';
import NativeTitle from './Parts/NativeTitle';
import { PlayerInterval } from 'utils/PlayerInterval/PlayerInterval';
import {
    getNativeMediaOverrides,
    useNativePost,
    baseStyles,
    getEntityStyles,
    StyledMeta,
    getNativeColor,
} from './Helpers';
import NativeFlex from './Bits/NativeFlex';
import { NativeStylingOptions, NativeColorMode } from '../../widgets/PostsWidget/types';

export type NativePostProps = {
    post: Post;
    playerInterval?: PlayerInterval;
    options?: NativeStylingOptions;
};
const Colors = {
    blue: '#2d8dc2',
};
const Theme = {
    light: {
        link: Colors.blue,
    },
    dark: {
        link: Colors.blue,
    },
};
export type StyledPostProps = {
    colorMode: NativeColorMode;
};
const StyledPost = styled(NativeFlex)`
    ${(props: NativePostProps) => baseStyles(props.options)}
    ${({ ratio }: any) => getNativeMediaOverrides(ratio > 2.3 ? 40 : 60, 60)}
    ${({ colorMode }: StyledPostProps) => getEntityStyles(getNativeColor(Theme, colorMode, 'link'))}
`;

const DefaultPost: React.FC<NativePostProps> = ({ post, playerInterval, options }) => {
    const {
        postRef,
        screenName,
        profileImgUrl,
        images,
        videos,
        name,
        snCreatedTime,
        snStats,
        unique,
        snType,
        textEntities,
        verified,
        hasMedia,
        text,
        rtl,
        language,
        orientation,
        ratio,
        location,
        title,
    } = useNativePost(post);
    const { lineClamp, maxCharacters, colorMode, removeUrls, emoji, hideZero, showTitle } = options;

    if (!post) {
        return <div key={unique} />;
    }

    const ProfileAndText = (
        <>
            {(profileImgUrl || name || screenName || snType) && (
                <NativeProfileLockup
                    colorMode={colorMode}
                    verified={verified}
                    name={name}
                    secondary={screenName ? `@${screenName}` : undefined}
                    snType={snType}
                    profileImgUrl={profileImgUrl}
                />
            )}
            {showTitle && !hasMedia && (
                <NativeTitle title={title} colorMode={colorMode} emoji={emoji} />
            )}
            {text && (
                <NativeText
                    removeUrls={removeUrls}
                    emoji={emoji}
                    colorMode={colorMode}
                    hasMedia={hasMedia}
                    lineClamp={lineClamp}
                    maxCharacters={maxCharacters}
                    channel={snType}
                    text={text}
                    rtl={rtl}
                    language={language}
                    textEntities={textEntities && textEntities.message}
                />
            )}
        </>
    );
    const timeStampAndEngagement = (
        <NativeFlex bottom vertical gap='s'>
            <NativeFlex middle gap='s' noGrow>
                {snCreatedTime && (
                    <StyledTimeStamp colorMode={colorMode}>
                        {getEndianTime(snCreatedTime, 'D MMM YYYY, h:mm a')}
                    </StyledTimeStamp>
                )}
                <StyledLocation colorMode={colorMode} location={location} />
            </NativeFlex>
            <NativeEngagement snStats={snStats} snType={snType} hideZero={hideZero} />
        </NativeFlex>
    );

    return (
        <StyledPost
            ratio={ratio}
            key={unique}
            ref={postRef}
            hasMedia={hasMedia}
            vertical={orientation === 'vertical'}
            orientation={orientation}
            options={options}
            colorMode={colorMode}
            gap
            pad>
            <>
                {orientation === 'vertical' && (
                    <>
                        {(profileImgUrl || name || screenName || snType) && (
                            <NativeProfileLockup
                                colorMode={colorMode}
                                verified={verified}
                                name={name}
                                secondary={screenName ? `@${screenName}` : undefined}
                                snType={snType}
                                profileImgUrl={profileImgUrl}
                            />
                        )}
                        {showTitle && !hasMedia && (
                            <NativeTitle title={title} colorMode={colorMode} emoji={emoji} />
                        )}
                        {hasMedia && (
                            <NativeMedia
                                snType={snType}
                                playerInterval={playerInterval}
                                images={images}
                                videos={videos}
                            />
                        )}
                        {text && (
                            <NativeText
                                removeUrls={removeUrls}
                                emoji={emoji}
                                colorMode={colorMode}
                                hasMedia={hasMedia}
                                lineClamp={lineClamp}
                                maxCharacters={maxCharacters}
                                channel={snType}
                                text={text}
                                textEntities={textEntities && textEntities.message}
                            />
                        )}
                        <NativeFlex vertical gap='s' bottom>
                            {timeStampAndEngagement}
                        </NativeFlex>
                    </>
                )}

                {orientation === 'horizontal' && (
                    <>
                        {hasMedia && (
                            <NativeMedia
                                snType={snType}
                                playerInterval={playerInterval}
                                images={images}
                                videos={videos}
                            />
                        )}

                        <StyledMeta vertical gap>
                            {ProfileAndText}
                            <NativeFlex bottom vertical gap='m'>
                                {timeStampAndEngagement}
                            </NativeFlex>
                        </StyledMeta>
                    </>
                )}
            </>
        </StyledPost>
    );
};

DefaultPost.displayName = 'DefaultPost';
export default observer(DefaultPost);
